import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DropDownMenu,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import {
  EditIcon,
  LinkIcon,
  MoreHorizontalIcon,
  PrinterIcon,
  TrashIcon,
} from "lucide-react";
import {
  Evidence,
  Label,
  EditEvidenceModal,
  DeleteEvidenceModal,
} from "@/Features";
import { PrintLabelsModal } from "@/Features/Labels/Modals/PrintLabelsModal";
import { SelectPrinterModal } from "@/Features/Labels";
import LabelsAPI from "@/api/labels/label_api";
import CasesApi from "@/api/cases";
import ClientsAPI from "@/api/clients";
import { useFeatures } from "@/contexts/FeaturesContext";
import { createEvidenceLabelAdapter } from "@/Features/Labels/evidenceLabelAdapter";
import type { LabelLookups } from "@/Features/Labels/evidenceLabelLookups";
import { usePermissions } from "@/hooks";
import CustomAttribute from "@/types/CustomAttribute";
import { ReportActionItem } from "@/Features/ChainOfCustody/Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { logActivity } from "@/api";

type HandleEditFinished = {
  evidenceInfo: Evidence;
  newAttributes: CustomAttribute[];
  updateData: Partial<Evidence>;
};

export type EvidenceActionButtonProps = {
  evidenceInfo: Evidence | null | undefined;
  onEditSubmit?: (data: HandleEditFinished) => void;
  onDelete?: (deletedEvidence: Evidence) => void;
  size?: "xs" | "xxs" | "sm" | "md" | "lg";
  enabled?: boolean;
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0px;
  align-items: center;
  justify-content: center;
`;

export const EvidenceActionButton: React.FC<EvidenceActionButtonProps> = ({
  evidenceInfo,
  onEditSubmit,
  onDelete,
  size = "xxs",
  enabled = true,
}) => {
  const theme = useTheme();
  const { hasPermission, currentUser, MONOLITH_PERMISSIONS } = usePermissions();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrintLabelModal, setShowPrintLabelModal] = useState(false);
  const { hasFeatures, FEATURES } = useFeatures();
  const bulkLabelPrinting = hasFeatures([FEATURES.BULK_LABEL_PRINTING]);

  // Single item print: the same dialog as bulk print, with this one item. The
  // template is picked in the dialog.
  const labelAdapter = useMemo(
    () =>
      evidenceInfo
        ? createEvidenceLabelAdapter(() => Promise.resolve([evidenceInfo]))
        : null,
    [evidenceInfo]
  );

  // The old single item print, kept while `bulk_label_printing` is off: a
  // template submenu, then SelectPrinterModal. Removed in ENG-2238.
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);

  const { data: labels } = useQuery({
    queryKey: ["item-labels"],
    queryFn: () => LabelsAPI.getLabels(),
    enabled: !bulkLabelPrinting && enabled && !!evidenceInfo,
  });

  const { data: caseData } = useQuery({
    queryKey: [
      "cases",
      "list",
      { case_id: evidenceInfo?.case_id, data_only: false },
    ],
    queryFn: () =>
      CasesApi.getCases({ case_id: evidenceInfo?.case_id, data_only: false }),
    enabled:
      !bulkLabelPrinting && showPrintLabelModal && !!evidenceInfo?.case_id,
  });

  const { data: clientData } = useQuery({
    queryKey: [
      "clients",
      "list",
      { case_id: evidenceInfo?.case_id, data_only: false },
    ],
    queryFn: () =>
      ClientsAPI.getClients({
        case_id: evidenceInfo?.case_id,
        data_only: false,
      }),
    enabled:
      !bulkLabelPrinting && showPrintLabelModal && !!evidenceInfo?.case_id,
  });

  const labelItems = (labels || []).map((label: Label) => ({
    label: label.label_name,
    value: label.label_id,
    disabled: !hasPermission(),
    onClick: () => {
      setSelectedLabel(label);
      setShowPrintLabelModal(true);
    },
  }));

  const caseRecord = caseData?.data?.[0];
  const clientRecord = clientData?.data?.[0];

  const closeLegacyPrint = () => {
    setShowPrintLabelModal(false);
    setSelectedLabel(null);
  };

  const cocActions: ReportActionItem[] = [
    {
      label: "Chain of Custody Report",
      value: "coc_report",
      onClick: async () => {
        const records = await EvidenceCocAPI.getCocRecords({
          evidence_id: evidenceInfo?.evidence_id as any,
        });
        EvidenceCocAPI.createEvidenceCocPDF({
          evidence: {
            ...evidenceInfo,
            currenUserFullName: currentUser.full_name,
          },
          cocData: records,
          options: { suppCoc: false },
        }).then(() => {
          logActivity(
            evidenceInfo?.case_id,
            currentUser.user_id,
            `Generated PDF COC Report for evidence item ${evidenceInfo?.evidence_number}.`
          );
        });
      },
    },
    {
      label: "Manual Chain of Custody",
      value: "manual_coc",
      onClick: async () => {
        const records = await EvidenceCocAPI.getCocRecords({
          evidence_id: evidenceInfo?.evidence_id as any,
        });
        EvidenceCocAPI.createEvidenceCocPDF({
          evidence: {
            ...evidenceInfo,
            currenUserFullName: currentUser.full_name,
          },
          cocData: records,
          options: { suppCoc: true },
        }).then(() => {
          logActivity(
            evidenceInfo?.case_id,
            currentUser.user_id,
            `Generated PDF COC Report for evidence item ${evidenceInfo?.evidence_number}.`
          );
        });
      },
    },
    {
      label: "Blank Intake Form",
      value: "intake_form",
      onClick: () => {
        EvidenceCocAPI.createEvidenceIntakePDF({
          evidence: {
            ...evidenceInfo,
            currenUserFullName: currentUser.full_name,
          },
        }).then(() => {
          logActivity(
            evidenceInfo?.case_id,
            currentUser.user_id,
            `Generated PDF Intake Form for evidence item ${evidenceInfo?.evidence_number}.`
          );
        });
      },
    },
    {
      label: "Blank Release Form",
      value: "release_form",
      onClick: () => {
        EvidenceCocAPI.createEvidenceReleasePDF({
          evidence: {
            ...evidenceInfo,
            currenUserFullName: currentUser.full_name,
          },
        }).then(() => {
          logActivity(
            evidenceInfo?.case_id,
            currentUser.user_id,
            `Generated PDF Release Form for evidence item ${evidenceInfo?.evidence_number}.`
          );
        });
      },
    },
  ];

  const handleEditSubmit = (data: HandleEditFinished) => {
    onEditSubmit?.(data);
    setShowEditModal(false);
  };

  const handleDelete = (deletedEvidence: Evidence) => {
    onDelete?.(deletedEvidence);
    setShowDeleteModal(false);
  };

  if (!evidenceInfo) {
    return null;
  }

  return (
    <StyledContainer>
      {showEditModal && (
        <EditEvidenceModal
          open={showEditModal}
          handleClose={() => setShowEditModal(false)}
          onEditSubmit={handleEditSubmit}
          defaultEvidenceInfo={evidenceInfo}
        />
      )}
      {showDeleteModal && (
        <DeleteEvidenceModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          evidenceInfo={evidenceInfo}
        />
      )}
      {hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE) && (
        <Button
          size={size}
          variant="subtle"
          leftSection={
            <EditIcon size={14} color={theme.palette.primary.main} />
          }
          onClick={() => setShowEditModal(true)}
        >
          Edit
        </Button>
      )}
      {hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_DELETE) && (
        <Button
          size={size}
          variant="subtle"
          leftSection={<TrashIcon size={14} color={theme.palette.error.main} />}
          onClick={() => setShowDeleteModal(true)}
        >
          Delete
        </Button>
      )}
      <DropDownMenu
        data={[
          {
            label: "Print Label",
            value: "print-label",
            leftSection: (
              <PrinterIcon size={14} color={theme.palette.text.secondary} />
            ),
            ...(bulkLabelPrinting
              ? {
                  disabled: !hasPermission(),
                  onClick: () => setShowPrintLabelModal(true),
                }
              : { items: labelItems }),
          },
          {
            label: "Chain of Custody",
            value: "chain-of-custody",
            leftSection: (
              <LinkIcon size={14} color={theme.palette.text.secondary} />
            ),
            items: cocActions,
          },
        ]}
        title={"Actions"}
        variant="subtle"
        size={size}
      >
        <MoreHorizontalIcon size={14} color={theme.palette.text.secondary} />
      </DropDownMenu>
      {bulkLabelPrinting && showPrintLabelModal && labelAdapter && (
        <PrintLabelsModal<Evidence, LabelLookups>
          open
          onClose={() => setShowPrintLabelModal(false)}
          {...labelAdapter}
        />
      )}
      {!bulkLabelPrinting && showPrintLabelModal && selectedLabel && (
        <SelectPrinterModal
          show={showPrintLabelModal}
          onClose={closeLegacyPrint}
          onCancel={closeLegacyPrint}
          data={{
            evidence: evidenceInfo,
            case: caseRecord,
            client: clientRecord,
          }}
          label={selectedLabel}
        />
      )}
    </StyledContainer>
  );
};
